import { useState } from "react";
import API from "../../../config/api";
import { buildWomanImageAlt } from "../../../utils/imageAlt";
import LanguageTabs, { getAdminLanguage } from "./LanguageTabs";

const TRANSLATED_FIELDS = ["achievement", "description"];

function WomanModal({
    woman,
    setWoman,
    categories,
    places,
    imagePreview,
    uploading,
    translating,
    onImageChange,
    onTranslate,
    onSave,
    onSaveAndTranslate,
    onClose,
}) {
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const [genNote, setGenNote] = useState("");
    const [activeLanguage, setActiveLanguage] = useState("ro");
    const language = getAdminLanguage(activeLanguage);
    const isRomanian = activeLanguage === "ro";
    const achievementField = `achievement${language.suffix}`;
    const descriptionField = `description${language.suffix}`;
    const busy = uploading || translating || generating;
    const canTranslate = Boolean(String(woman.achievement || "").trim() || String(woman.description || "").trim());

    const updateField = (field, value) => {
        setWoman((current) => ({ ...current, [field]: value }));
    };

    const handleGenerate = async () => {
        if (!woman.name || !woman.surname) {
            setGenError("Completați numele și prenumele înainte de generare.");
            return;
        }
        setGenError("");
        setGenNote("");
        setGenerating(true);

        const category = categories.find((c) => String(c.id) === String(woman.category_id))?.name || "";
        const place = places.find((p) => String(p.id) === String(woman.place_id))?.name || "";

        try {
            const res = await fetch(`${API.admin}?action=generateDescription`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: woman.name,
                    surname: woman.surname,
                    category,
                    place,
                    description: woman.description || "",
                }),
            });
            const data = await res.json();
            if (data.success) {
                const foundSources = Array.isArray(data.sources) ? data.sources : [];

                setWoman((current) => {
                    const existing = String(current.sources || "")
                        .split("\n")
                        .map((line) => line.trim())
                        .filter(Boolean);
                    const merged = [...existing];

                    foundSources.forEach((url) => {
                        if (!merged.some((line) => line.includes(url))) {
                            merged.push(url);
                        }
                    });

                    return { ...current, description: data.description, sources: merged.join("\n") };
                });
                setGenNote(
                    foundSources.length > 0
                        ? `Text generat. Am adăugat ${foundSources.length} surse găsite - verifică-le înainte de salvare.`
                        : "Text generat, dar fără surse. Verifică informațiile și adaugă sursele manual.",
                );
            } else {
                setGenError(data.error || "Eroare la generare");
            }
        } catch {
            setGenError("Eroare de conexiune");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="admin__modal-overlay" onClick={onClose}>
            <div className="admin__modal admin__modal--wide card-common" onClick={(e) => e.stopPropagation()}>
                <div className="admin__modal-title-row">
                    <h2>{woman.id ? "Editează" : "Adaugă"} Femeie</h2>
                    <button
                        type="button"
                        className="admin__btn-ai"
                        onClick={onTranslate}
                        disabled={!canTranslate || busy}
                        title="Completează filele RUS și ENG pornind de la textul în română. Nu salvează nimic.">
                        {translating ? "Se traduce..." : "Tradu cu AI"}
                    </button>
                </div>
                <form onSubmit={onSave}>
                    <div className="admin__form-grid">
                        <div className="admin__field">
                            <label>Nume *</label>
                            <input
                                type="text"
                                value={woman.name}
                                onChange={(e) => updateField("name", e.target.value)}
                                required
                            />
                        </div>
                        <div className="admin__field">
                            <label>Prenume *</label>
                            <input
                                type="text"
                                value={woman.surname}
                                onChange={(e) => updateField("surname", e.target.value)}
                                required
                            />
                        </div>
                        <div className="admin__field">
                            <label>Categorie</label>
                            <select
                                value={woman.category_id || ""}
                                onChange={(e) => updateField("category_id", e.target.value)}>
                                <option value="">-- Selectează --</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin__field">
                            <label>Loc</label>
                            <select value={woman.place_id || ""} onChange={(e) => updateField("place_id", e.target.value)}>
                                <option value="">-- Selectează --</option>
                                {places.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin__field full">
                            <label>Imagine</label>
                            <div className="admin__image-upload">
                                {imagePreview && (
                                    <img
                                        src={imagePreview}
                                        alt={buildWomanImageAlt({
                                            name: woman.name,
                                            surname: woman.surname,
                                            category: categories.find((c) => String(c.id) === String(woman.category_id))
                                                ?.name,
                                            place: places.find((p) => String(p.id) === String(woman.place_id))?.name,
                                        })}
                                        className="admin__image-preview"
                                    />
                                )}
                                <input type="file" accept="image/*" onChange={onImageChange} />
                                {woman.image && !imagePreview?.startsWith("blob:") && (
                                    <span className="admin__current-image">Curent: {woman.image}</span>
                                )}
                            </div>
                        </div>
                        <div className="admin__field full">
                            <label className="admin__checkbox-field">
                                <input
                                    type="checkbox"
                                    checked={parseInt(woman.is_monthly_candidate || 0) === 1}
                                    onChange={(e) => updateField("is_monthly_candidate", e.target.checked ? 1 : 0)}
                                />
                                <span>Poate fi selectată ca femeia lunii</span>
                            </label>
                        </div>

                        <div className="admin__field full">
                            <LanguageTabs
                                active={activeLanguage}
                                onChange={setActiveLanguage}
                                values={woman}
                                fields={TRANSLATED_FIELDS}
                                ariaLabel="Limbi profil"
                            />

                            <div className="admin__language-panel" role="tabpanel">
                                <div className="admin__field">
                                    <label>Realizare principală ({language.name})</label>
                                    <textarea
                                        value={woman[achievementField] || ""}
                                        onChange={(e) => updateField(achievementField, e.target.value)}
                                        rows={3}
                                        placeholder={
                                            isRomanian
                                                ? "Cea mai importantă realizare a persoanei..."
                                                : "Gol = pe site se afișează textul în română"
                                        }
                                    />
                                </div>
                                <div className="admin__field">
                                    <div className="admin__field-header">
                                        <label>Descriere ({language.name})</label>
                                        {isRomanian && (
                                            <button
                                                type="button"
                                                className="admin__btn-ai"
                                                onClick={handleGenerate}
                                                disabled={busy}>
                                                {generating ? "⏳ Se generează..." : "✨ Generează cu AI"}
                                            </button>
                                        )}
                                    </div>
                                    {isRomanian && genError && <p className="admin__gen-error">{genError}</p>}
                                    {isRomanian && genNote && <p className="admin__gen-note">{genNote}</p>}
                                    <textarea
                                        value={woman[descriptionField] || ""}
                                        onChange={(e) => updateField(descriptionField, e.target.value)}
                                        rows={9}
                                        placeholder={isRomanian ? "" : "Gol = pe site se afișează textul în română"}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="admin__field full">
                            <label>Surse (câte una pe rând)</label>
                            <textarea
                                value={woman.sources || ""}
                                onChange={(e) => updateField("sources", e.target.value)}
                                rows={4}
                                placeholder={
                                    "https://exemplu.md/articol\nNume publicație | https://exemplu.md/interviu"
                                }
                            />
                            <p className="admin__hint">
                                Linkuri către paginile pe care se bazează profilul: site oficial, presă, profil
                                profesional. Apar public în josul profilului, la „Surse”.
                            </p>
                        </div>
                    </div>
                    <div className="admin__modal-actions">
                        <button type="button" onClick={onClose} disabled={busy}>
                            Anulează
                        </button>
                        <button
                            type="button"
                            className="secondary"
                            onClick={onSaveAndTranslate}
                            disabled={busy || !canTranslate}
                            title="Traduce textul din română în rusă și engleză (suprascrie filele RUS/ENG), apoi salvează.">
                            {translating ? "Se traduce..." : "Salvează + tradu cu AI"}
                        </button>
                        <button
                            type="submit"
                            className="primary"
                            disabled={busy}
                            title="Salvează exact ce este scris în cele trei file, fără traducere automată.">
                            {uploading ? "Se salvează..." : "Salvează"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default WomanModal;
